import { Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { CARRIER_ADMITTED_STATUS_LABELS, CarrierAdmittedStatus } from '@/models/insuranceCarrier'

// Radix treats an empty string as "no selection" and refuses to render an item with one.
const NONE = 'none'

export interface EditableAlias {
  id?: string
  name: string
  admittedStatus: CarrierAdmittedStatus | ''
  notes: string
}

export function emptyAlias(): EditableAlias {
  return { name: '', admittedStatus: '', notes: '' }
}

interface CarrierAliasRowsProps {
  aliases: EditableAlias[]
  onChange: (aliases: EditableAlias[]) => void
}

export function CarrierAliasRows({ aliases, onChange }: CarrierAliasRowsProps) {
  function update(index: number, patch: Partial<EditableAlias>) {
    onChange(aliases.map((alias, i) => (i === index ? { ...alias, ...patch } : alias)))
  }

  function remove(index: number) {
    onChange(aliases.filter((_, i) => i !== index))
  }

  function add() {
    onChange([...aliases, emptyAlias()])
  }

  return (
    <div className="flex flex-col gap-3">
      <Label>Aliases</Label>
      <p className="text-muted-foreground text-sm">
        Other names a vendor or document uses for this carrier. Set an insurance type when the alias means a
        specific paper — offers resolved through it are stamped with it.
      </p>
      {aliases.length === 0 && (
        <p className="text-muted-foreground text-sm">
          No aliases yet — this carrier only answers to its own name.
        </p>
      )}
      {aliases.map((alias, index) => (
        <div key={index} className="flex flex-col gap-2 rounded-md border p-3 sm:flex-row sm:items-center">
          <Input
            className="sm:flex-1"
            placeholder="Name a vendor sends"
            value={alias.name}
            onChange={(e) => update(index, { name: e.target.value })}
          />
          <Select
            value={alias.admittedStatus || NONE}
            onValueChange={(next) =>
              update(index, { admittedStatus: next === NONE ? '' : (next as CarrierAdmittedStatus) })
            }
          >
            <SelectTrigger className="sm:w-44" aria-label="Insurance type for this alias">
              <SelectValue placeholder="Inherit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NONE}>Inherit</SelectItem>
              {Object.values(CarrierAdmittedStatus).map((status) => (
                <SelectItem key={status} value={status}>
                  {CARRIER_ADMITTED_STATUS_LABELS[status]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex justify-end sm:shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => remove(index)}
              aria-label="Remove alias"
            >
              <Trash2 className="size-4" />
            </Button>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add} className="self-start">
        Add alias
      </Button>
    </div>
  )
}
